import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSettingParams } from '../../../common/filterCompany/useSettingParams'
import { Element } from '../../../common/html/element'
import { Loading } from '../../../common/loading'
import { Panel } from '../../../common/panel'
import { ScrollComponent } from '../../../common/scrollComponent'
import { selectPeriod } from '../../common/period/slice'
import { VALUE_SCORE } from '../constant'
import { selectScoreActive } from '../store/slice'
import { BoxItem } from './BoxItem'
import classes from './index.module.scss'
import {
  BOX_KEY,
  getDataTableBiggest,
  getDataTableHighest,
  getDataTableLargest,
  getDataTablePerBiggest,
  getDataTablePerHighest,
  getDataTablePerLargest,
  getLoading,
} from './store/slice'
import {
  exportTopCreditRiskScore,
  exportTopPerformanceScore,
  getBiggestCreditRisk,
  getBiggestPerformance,
  getHighestCreditRisk,
  getHighestPerformance,
  getLargestDeclineCreditRisk,
  getLargestDeclinePerformance,
} from './store/thunk'

const TEXT_BY_TYPE_SCORE = {
  [VALUE_SCORE.CREDIT_SCORE]: {
    title: 'portfolioScore.titleCompaniesByRisk',
    1: {
      title: 'portfolioScore.highestCreditRisk',
      textNote: 'portfolioScore.hightTextNoteCompanies',
    },
    2: {
      title: 'portfolioScore.biggestScoreChanges',
      textNote: 'portfolioScore.biggestCompaniesTextNote',
    },
    3: {
      title: 'portfolioScore.largestDecline',
      textNote: 'portfolioScore.companiesWithLargestText',
    },
  },
  [VALUE_SCORE.PERFORMANCE]: {
    title: 'portfolioScore.titleCompaniesByPerformance',
    1: {
      title: 'portfolioScore.highestGrowthByPer',
      textNote: 'portfolioScore.companiesHighestPerformance',
    },
    2: {
      title: 'portfolioScore.biggestScoreChanges',
      textNote: 'portfolioScore.companiesBiggestPer',
    },
    3: {
      title: 'portfolioScore.largestDecline',
      textNote: 'portfolioScore.companiesLargestByPer',
    },
  },
}

const LIMIT_COMPANY = 10

export const TopCompanies = ({ isDynamicReport, settings, boxKey, type }) => {
  const dispatch = useDispatch()
  const settingParams = useSettingParams()
  const { value: typeValueStore } = useSelector(selectScoreActive)
  const period = useSelector(selectPeriod)
  const loading = useSelector(getLoading)
  const locale = useSelector((state) => state.i18n.locale)

  const typeValue = isDynamicReport ? type : typeValueStore

  const params = {
    timeFrequency: period,
    limit: LIMIT_COMPANY,
    ...settingParams,
  }

  useEffect(() => {
    if (isDynamicReport) return
    if (typeValue === VALUE_SCORE.PERFORMANCE) {
      dispatch(getHighestPerformance(params))
      dispatch(getBiggestPerformance(params))
      dispatch(getLargestDeclinePerformance(params))
    }
    if (typeValue === VALUE_SCORE.CREDIT_SCORE) {
      dispatch(getHighestCreditRisk(params))
      dispatch(getBiggestCreditRisk(params))
      dispatch(getLargestDeclineCreditRisk(params))
    }
  }, [period, typeValue, settingParams, locale])

  if (isDynamicReport) {
    switch (boxKey) {
      case 'highestCreditRisk':
        return (
          <BoxItem
            text={TEXT_BY_TYPE_SCORE[typeValue][1]}
            keyIds={BOX_KEY.HIGHEST}
            getDataRedux={
              typeValue === VALUE_SCORE.CREDIT_SCORE
                ? getDataTableHighest
                : getDataTablePerHighest
            }
            isDynamicReport
            typeValue={typeValue}
            isShowNoData={settings?.[0] === boxKey}
          />
        )
      case 'biggestScoreChanges':
        return (
          <BoxItem
            text={TEXT_BY_TYPE_SCORE[typeValue][2]}
            keyIds={BOX_KEY.BIGGEST}
            getDataRedux={
              typeValue === VALUE_SCORE.CREDIT_SCORE
                ? getDataTableBiggest
                : getDataTablePerBiggest
            }
            isDynamicReport
            typeValue={typeValue}
            isShowNoData={settings?.[0] === boxKey}
          />
        )
      case 'largestDecline':
        return (
          <BoxItem
            text={TEXT_BY_TYPE_SCORE[typeValue][3]}
            keyIds={BOX_KEY.LARGEST}
            getDataRedux={
              typeValue === VALUE_SCORE.CREDIT_SCORE
                ? getDataTableLargest
                : getDataTablePerLargest
            }
            isDynamicReport
            typeValue={typeValue}
            isShowNoData={settings?.[0] === boxKey}
          />
        )
      default:
        return null
    }
  }

  return (
    <Panel
      label={TEXT_BY_TYPE_SCORE[typeValue].title}
      isI18n
      downloadCsv={{
        downloadFunc: () =>
          typeValue === VALUE_SCORE.CREDIT_SCORE
            ? exportTopCreditRiskScore(params)
            : exportTopPerformanceScore(params),
        disabled: loading,
      }}
    >
      {loading ? (
        <div className="positionRelative" style={{ height: 200 }}>
          <Loading />
        </div>
      ) : (
        <ScrollComponent autoHeight autoHeightMax={1000}>
          <div className={classes.container}>
            <BoxItem
              text={TEXT_BY_TYPE_SCORE[typeValue][1]}
              keyIds={BOX_KEY.HIGHEST}
              getDataRedux={
                typeValue === VALUE_SCORE.CREDIT_SCORE
                  ? getDataTableHighest
                  : getDataTablePerHighest
              }
              typeValue={typeValue}
            />
            <Element
              className={classes.border}
              style={{ backgroundColor: '#D5D5D5' }}
            />
            <BoxItem
              text={TEXT_BY_TYPE_SCORE[typeValue][2]}
              keyIds={BOX_KEY.BIGGEST}
              getDataRedux={
                typeValue === VALUE_SCORE.CREDIT_SCORE
                  ? getDataTableBiggest
                  : getDataTablePerBiggest
              }
              typeValue={typeValue}
            />
            <Element
              className={classes.border}
              style={{ backgroundColor: '#D5D5D5' }}
            />
            <BoxItem
              text={TEXT_BY_TYPE_SCORE[typeValue][3]}
              keyIds={BOX_KEY.LARGEST}
              getDataRedux={
                typeValue === VALUE_SCORE.CREDIT_SCORE
                  ? getDataTableLargest
                  : getDataTablePerLargest
              }
              typeValue={typeValue}
            />
          </div>
        </ScrollComponent>
      )}
    </Panel>
  )
}

TopCompanies.propTypes = {
  isDynamicReport: PropTypes.bool,
  setting: PropTypes.array,
}

TopCompanies.defaultProps = {
  isDynamicReport: false,
  setting: [],
}
